import { makeStyles } from '@material-ui/styles';
import { Field, Formik } from 'formik';
import gql from 'graphql-tag';
import pick from 'lodash/pick';
import React, { useContext, useState } from 'react';

import { Alert } from 'src/common/ui/components';
import DateControl from 'src/components/forms/controls/date';
import { PrimaryButton } from 'src/components/general/PrimaryButton';
import { SecondaryButton } from 'src/components/general/SecondaryButton';
import useTypedSWR from 'src/components/general/useTypedSWR';
import { ApolloClientContext } from 'src/data/ApolloClientContext';
import { GetIdVerificationDataQuery } from 'src/generated/gql/graphql';
import { GET_ID_VERIFICATION_STATUSES } from 'src/idVerification/queries/getIdVerificationStatuses.gql';
import { SelectControl } from 'src/nightingale/components/ChartProperty/controls/Select/SelectControl';

export const EditStatusReviewForm: React.FC<{
  patientId: string;
  verificationData: GetIdVerificationDataQuery['staff_getIdVerificationData'];
  onClose: () => void;
  mutate: () => void;
  // mutate: () => void;
}> = ({ patientId, verificationData, onClose, mutate }) => {
  const classes = useStyles();
  const [updateError, setUpdateError] = useState<Error | null>(null);
  const { apolloClient } = useContext(ApolloClientContext);

  const { data } = useTypedSWR(GET_ID_VERIFICATION_STATUSES);

  const onSave = async (values: GetIdVerificationDataQuery['staff_getIdVerificationData']) => {
    if (!apolloClient) {
      return;
    }

    try {
      await apolloClient.mutate({
        mutation: gql`
          mutation ($patientId: String!, $updates: IdVerificationUpdates!) {
            staff_updateIdVerification(patientId: $patientId, updates: $updates)
          }
        `,
        variables: {
          patientId,
          updates: {
            status: values.identityVerificationStatus
              ? pick(values.identityVerificationStatus, 'value', 'label')
              : null,
            dueDate: values.identityVerificationDueDate,
          },
        },
      });

      mutate();
    } catch (e) {
      setUpdateError(e);
    }
  };

  return (
    <>
      {updateError && (
        <Alert title="Error updating verification status" error={updateError} severity="error" />
      )}
      <Formik
        initialValues={verificationData}
        onSubmit={(values, actions) =>
          onSave(values).finally(() => {
            actions.setSubmitting(false);
            onClose();
          })
        }
      >
        {({ handleSubmit, isSubmitting, dirty }) => {
          return (
            <form className={classes.form} onSubmit={handleSubmit}>
              <Field
                name="identityVerificationStatus"
                options={data?.staff_idVerificationStatuses}
                component={StatusSelect}
              />
              <Field name="identityVerificationDueDate" label="Due Date" component={DateControl} />

              <span className={classes.buttons}>
                <div>
                  <SecondaryButton onClick={() => onClose()}>Cancel</SecondaryButton>
                  <PrimaryButton
                    className={classes.button}
                    disabled={isSubmitting || !dirty}
                    type="submit"
                  >
                    Save
                  </PrimaryButton>
                </div>
              </span>
            </form>
          );
        }}
      </Formik>
    </>
  );
};

const StatusSelect = ({ form, field, options }) => (
  <SelectControl
    label="Status"
    name="identityVerificationStatus"
    options={options}
    value={field.value}
    onChangeValue={newVal => {
      form.setFieldValue(field.name, newVal);
    }}
  />
);

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: 30,
    '& .MuiFormControl-root': {
      marginBottom: 16,
    },
  },
  buttons: {
    display: 'flex',
    flexDirection: 'row-reverse',
    marginTop: 20,
  },
  button: {
    marginLeft: 8,
  },
});
